import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

// Live updates between people on the app at the same time — the client side of
// server/services/live.ts. One EventSource per tab:
//   - `change` events (something was saved by someone else) → useLiveChanges;
//   - `presence` events (who else is on this screen, and which card each one is
//     moving or has open) → usePresence;
//   - `draft` / `typing` events (what someone is typing, before saving) →
//     useLiveDraft, useTypingIn;
//   - `view` / `followers` / `follow-end` events ("Mode collaborateur": the
//     others follow one person's screen) → useCollab, useFollowedState.
// Requests carry this tab's id (X-Live-Conn, see api/client.ts) so its own
// saves don't come back to it as changes.

export interface LiveChange { area: 'tasks' | 'project' | 'kanban'; projectId?: number | null; boardId?: number | null; actor?: string | null }
// over: while dragging, the cell (task board) or column (Kanban Fonction) the card is over —
// the others see a ghost of the card follow it there.
export interface LiveFocus { type: 'task' | 'card'; id: number; action: 'drag' | 'open' | 'moved'; over?: string }
// presentingConn: the person presents from a tab on this screen (collaborator mode).
export interface LivePerson { userId: number; name: string; focus: LiveFocus | null; presentingConn?: string }
// Someone's pointer: where it is inside the element marked data-live-anchor="…" (fractions of its size).
export interface LiveCursor { userId: number; name: string; anchor: string | null; fx: number; fy: number }

let source: EventSource | null = null;
let conn: string | null = null;
let room: string | null = null;
let focus: LiveFocus | null = null;
const changeListeners = new Set<(c: LiveChange) => void>();
const presenceListeners = new Set<(room: string, people: LivePerson[]) => void>();
const cursorListeners = new Set<(c: LiveCursor) => void>();

export const getLiveConn = () => conn;

/** Opens the stream once the user is logged in (the browser reconnects it by itself). */
export function startLive(): void {
  if (source || typeof EventSource === 'undefined') return;
  source = new EventSource('/api/live/stream', { withCredentials: true });
  listenDrafts(source);
  listenCollab(source);
  source.addEventListener('hello', (e) => {
    conn = JSON.parse((e as MessageEvent).data).conn;
    sendPresence(); // after a reconnect too: the server forgot this tab
  });
  source.addEventListener('change', (e) => {
    const change = JSON.parse((e as MessageEvent).data) as LiveChange;
    changeListeners.forEach(fn => fn(change));
  });
  source.addEventListener('cursor', (e) => {
    const c = JSON.parse((e as MessageEvent).data) as LiveCursor;
    cursorListeners.forEach(fn => fn(c));
  });
  source.addEventListener('presence', (e) => {
    const { room: r, people } = JSON.parse((e as MessageEvent).data);
    presenceListeners.forEach(fn => fn(r, people));
    if (r === room) onRoomPeople(people);
  });
}

export function stopLive(): void {
  source?.close();
  source = null;
  conn = null;
}

function sendPresence(): void {
  if (!conn) return;
  fetch('/api/live/presence', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conn, room, focus }),
  }).catch(() => {});
}

/** The card this tab is moving or has open (null: none) — shown to the others as initials. */
export function setLiveFocus(next: LiveFocus | null): void {
  if (JSON.stringify(next) === JSON.stringify(focus)) return;
  focus = next;
  sendPresence();
}

/**
 * Calls `onChange` when someone else saves something — debounced, so a burst
 * of saves (a drag moves several cards) refetches once.
 */
export function useLiveChanges(onChange: (c: LiveChange) => void, delayMs = 400): void {
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let pending: LiveChange | null = null;
    const listener = (c: LiveChange) => {
      pending = c;
      clearTimeout(timer);
      timer = setTimeout(() => { if (pending) cb.current(pending); pending = null; }, delayMs);
    };
    changeListeners.add(listener);
    return () => { changeListeners.delete(listener); clearTimeout(timer); };
  }, [delayMs]);
}

/** Puts this tab in `room` while mounted; returns the other people there (not me). */
export function usePresence(roomId: string | null, myUserId: number | undefined): LivePerson[] {
  const [people, setPeople] = useState<LivePerson[]>([]);
  useEffect(() => {
    if (!roomId) return;
    room = roomId;
    focus = null;
    sendPresence();
    const listener = (r: string, list: LivePerson[]) => { if (r === roomId) setPeople(list); };
    presenceListeners.add(listener);
    return () => {
      presenceListeners.delete(listener);
      if (room === roomId) { room = null; focus = null; sendPresence(); }
      setPeople([]);
    };
  }, [roomId]);
  return people.filter(p => p.userId !== myUserId);
}

/** "Sofia Mitev" → "SM". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** Who has a given card in focus: id → people. */
export function focusMap(people: LivePerson[], type: LiveFocus['type']): Map<number, LivePerson[]> {
  const map = new Map<number, LivePerson[]>();
  for (const p of people) {
    if (p.focus?.type !== type) continue;
    map.set(p.focus.id, [...(map.get(p.focus.id) ?? []), p]);
  }
  return map;
}

let movedTimer: ReturnType<typeof setTimeout> | undefined;
/** After a drop: the others see "moved by …" on the card for a few seconds, then `after`. */
export function flashMoved(type: LiveFocus['type'], id: number, after: LiveFocus | null = null): void {
  clearTimeout(movedTimer);
  setLiveFocus({ type, id, action: 'moved' });
  movedTimer = setTimeout(() => {
    if (focus?.action === 'moved' && focus.id === id) setLiveFocus(after);
  }, 4000);
}

/** The people dragging a card over `over` (a cell or column id) — to draw its ghost there. */
export function draggingOver(people: LivePerson[], type: LiveFocus['type'], over: string): LivePerson[] {
  return people.filter(p => p.focus?.type === type && p.focus.action === 'drag' && p.focus.over === over);
}

// This tab's pointer, at most ~8 times a second (the last position always goes out).
let cursorTimer: ReturnType<typeof setTimeout> | undefined;
let cursorPending: { anchor: string | null; fx: number; fy: number } | null = null;
let cursorSentAt = 0;
export function sendCursor(anchor: string | null, fx = 0, fy = 0): void {
  if (!conn || !room) return;
  cursorPending = { anchor, fx, fy };
  const flush = () => {
    if (!cursorPending || !conn) return;
    const body = JSON.stringify({ conn, ...cursorPending });
    cursorPending = null;
    cursorSentAt = Date.now();
    fetch('/api/live/cursor', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body }).catch(() => {});
  };
  clearTimeout(cursorTimer);
  const wait = 125 - (Date.now() - cursorSentAt);
  if (wait <= 0) flush(); else cursorTimer = setTimeout(flush, wait);
}

export function onLiveCursor(fn: (c: LiveCursor) => void): () => void {
  cursorListeners.add(fn);
  return () => { cursorListeners.delete(fn); };
}

// ── Live drafts ──
// What someone is typing in a text field, before it is saved (server side:
// "Live drafts" in server/services/live.ts). A field has a key saying what it
// belongs to — task:<id>:<field>, todo:<id>:<field>, card:<id>:<field>,
// project:<id>:<field>. The others with the same thing open see the text as it
// is typed, under their own field (their own text is never touched).

export interface LiveDraft { userId: number; name: string; text: string }

const draftsByKey = new Map<string, Map<number, LiveDraft>>();
const draftListeners = new Set<(key: string) => void>();
const watchCounts = new Map<string, number>();
let watchTimer: ReturnType<typeof setTimeout> | undefined;
// entity ("task:12", "card:4") → who is typing in it, for the ✎ on board cards.
const typingByEntity = new Map<string, { userId: number; name: string }[]>();
const typingListeners = new Set<() => void>();

function post(path: string, body: unknown): void {
  fetch(path, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => {});
}

function sendWatch(): void {
  clearTimeout(watchTimer);
  watchTimer = setTimeout(() => { if (conn) post('/api/live/watch', { conn, keys: [...watchCounts.keys()] }); }, 50);
}

function onDraftEvent(e: Event): void {
  const { key, userId, name, text } = JSON.parse((e as MessageEvent).data) as { key: string; userId: number; name: string; text: string | null };
  const byUser = draftsByKey.get(key) ?? new Map<number, LiveDraft>();
  if (text == null) byUser.delete(userId); else byUser.set(userId, { userId, name, text });
  if (byUser.size) draftsByKey.set(key, byUser); else draftsByKey.delete(key);
  draftListeners.forEach(fn => fn(key));
}

function onTypingEvent(e: Event): void {
  const { entity, people } = JSON.parse((e as MessageEvent).data) as { entity: string; people: { userId: number; name: string }[] };
  if (people.length) typingByEntity.set(entity, people); else typingByEntity.delete(entity);
  typingListeners.forEach(fn => fn());
}

/** Hooks the draft events on the stream (called by startLive). */
function listenDrafts(es: EventSource): void {
  es.addEventListener('draft', onDraftEvent);
  es.addEventListener('typing', onTypingEvent);
  es.addEventListener('hello', () => {
    // A new stream: the server forgot this tab's fields and drafts.
    draftsByKey.clear();
    typingByEntity.clear();
    draftListeners.forEach(fn => fn(''));
    typingListeners.forEach(fn => fn());
    if (watchCounts.size) sendWatch();
  });
}

/**
 * A live text field: while `editing` (it has the focus), sends what is typed
 * (at most ~6 times a second); returns what the others are typing in it.
 * key null: not live.
 */
export function useLiveDraft(key: string | null, value: string, editing: boolean, myUserId?: number): LiveDraft[] {
  const [, rerender] = useState(0);

  // Watch the key while mounted.
  useEffect(() => {
    if (!key) return;
    watchCounts.set(key, (watchCounts.get(key) ?? 0) + 1);
    sendWatch();
    const listener = (k: string) => { if (k === key || k === '') rerender(n => n + 1); };
    draftListeners.add(listener);
    return () => {
      draftListeners.delete(listener);
      const n = (watchCounts.get(key) ?? 1) - 1;
      if (n > 0) watchCounts.set(key, n); else { watchCounts.delete(key); sendWatch(); }
    };
  }, [key]);

  // Send my text while I type; nothing until it differs from what the field had on focus.
  const startValue = useRef<string | null>(null);
  const sent = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastSentAt = useRef(0);
  useEffect(() => {
    if (!key) return;
    if (!editing) { startValue.current = null; return; }
    if (startValue.current == null) { startValue.current = value; return; }
    if (value === startValue.current && !sent.current) return;
    const flush = () => {
      if (!conn) return;
      lastSentAt.current = Date.now();
      sent.current = true;
      post('/api/live/draft', { conn, key, text: value });
    };
    clearTimeout(timer.current);
    const wait = 160 - (Date.now() - lastSentAt.current);
    if (wait <= 0) flush(); else timer.current = setTimeout(flush, wait);
  }, [key, value, editing]);

  // Stopped typing (focus left, or the field went away): the others' preview goes.
  useEffect(() => {
    if (editing || !key) return;
    clearTimeout(timer.current);
    if (sent.current && conn) post('/api/live/draft', { conn, key, text: null });
    sent.current = false;
  }, [key, editing]);
  useEffect(() => () => {
    clearTimeout(timer.current);
    if (key && sent.current && conn) post('/api/live/draft', { conn, key, text: null });
    sent.current = false;
  }, [key]);

  if (!key) return [];
  return [...(draftsByKey.get(key)?.values() ?? [])].filter(d => d.userId !== myUserId);
}

/** Who is typing in which task (type 'task') or Kanban card (type 'card'): id → people. */
export function useTypingIn(type: 'task' | 'card', myUserId?: number): Map<number, { userId: number; name: string }[]> {
  const [, rerender] = useState(0);
  useEffect(() => {
    const listener = () => rerender(n => n + 1);
    typingListeners.add(listener);
    return () => { typingListeners.delete(listener); };
  }, []);
  const map = new Map<number, { userId: number; name: string }[]>();
  for (const [entity, people] of typingByEntity) {
    const [t, id] = entity.split(':');
    const others = people.filter(p => p.userId !== myUserId);
    if (t === type && others.length) map.set(Number(id), others);
  }
  return map;
}

// ── Collaborator mode ──
// One person turns on "Mode collaborateur" (startPresenting): the others on the
// same screen follow that tab automatically — its page (hash), what it opens or
// folds (useFollowedState), its scroll (Collab.tsx) — until they stop following
// (they can follow again) or the presenter stops. Server side: "Collaborator
// mode" in server/services/live.ts.

export interface CollabState {
  presenting: boolean;
  /** People following my screen (when presenting). */
  followers: { userId: number; name: string }[];
  /** The presenter I follow (null: none). */
  following: { conn: string; userId: number; name: string } | null;
  /** Someone presenting on my screen whom I don't follow (I stopped): offers to follow again. */
  offer: { conn: string; userId: number; name: string } | null;
}

let myUserId: number | null = null;
let collab: CollabState = { presenting: false, followers: [], following: null, offer: null };
const collabListeners = new Set<() => void>();
const presenterView = new Map<string, unknown>(); // the followed presenter's keys → values
const viewListeners = new Set<(key: string | null) => void>(); // key null: everything changed
const declined = new Set<string>(); // presenting tabs I chose not to follow
const refused = new Set<string>(); // presenting tabs the server won't let me follow (not part of the project)
// A follow asked for, not yet accepted: its catch-up view is taken in, but nothing
// shows (bar, followed states) until the server says yes.
let pendingFollow: string | null = null;
// Followed states shown on my screen: key → its current value (to publish when I start presenting).
const mountedStates = new Map<string, Set<() => unknown>>();

function setCollab(next: Partial<CollabState>): void {
  collab = { ...collab, ...next };
  collabListeners.forEach(fn => fn());
}

/** The current collaborator-mode state (outside React). */
export const getCollab = (): CollabState => collab;

/** The current collaborator-mode state, re-rendering on change. */
export function useCollab(): CollabState {
  const [, rerender] = useState(0);
  useEffect(() => {
    const listener = () => rerender(n => n + 1);
    collabListeners.add(listener);
    return () => { collabListeners.delete(listener); };
  }, []);
  return collab;
}

// The presenter's changes go out together, at most every 120 ms.
let viewPending: Record<string, unknown> = {};
let viewTimer: ReturnType<typeof setTimeout> | undefined;
/** As presenter: `key` now shows `value` on my screen. */
export function publishView(key: string, value: unknown): void {
  if (!collab.presenting) return;
  viewPending[key] = value === undefined ? null : value;
  if (viewTimer) return;
  viewTimer = setTimeout(() => {
    viewTimer = undefined;
    const patch = viewPending;
    viewPending = {};
    if (conn && collab.presenting && Object.keys(patch).length) post('/api/live/view', { conn, patch });
  }, 120);
}

function publishAllMounted(): void {
  for (const [key, getters] of mountedStates) for (const get of getters) publishView(key, get());
}

function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}

/** Starts presenting: the others on this screen follow me. */
export function startPresenting(): void {
  if (!conn) return;
  if (collab.following || pendingFollow) post('/api/live/follow', { conn, presenter: null });
  pendingFollow = null;
  presenterView.clear();
  setCollab({ presenting: true, following: null, offer: null, followers: [] });
  postJson('/api/live/present', { conn, on: true })
    .then((r) => {
      if (!r.ok) { setCollab({ presenting: false, followers: [] }); return; }
      publishAllMounted();
      window.dispatchEvent(new Event('pmo:collab-publish'));
    })
    .catch(() => {});
}

export function stopPresenting(): void {
  if (conn) post('/api/live/present', { conn, on: false });
  setCollab({ presenting: false, followers: [] });
}

function follow(target: { conn: string; userId: number; name: string }): void {
  if (!conn) return;
  presenterView.clear();
  pendingFollow = target.conn;
  postJson('/api/live/follow', { conn, presenter: target.conn })
    .then(r => {
      if (pendingFollow !== target.conn) return; // overtaken (I started presenting…)
      pendingFollow = null;
      if (!r.ok) { refused.add(target.conn); presenterView.clear(); return; }
      setCollab({ following: target, offer: null });
      viewListeners.forEach(fn => fn(null)); // now apply the catch-up
    })
    .catch(() => { if (pendingFollow === target.conn) pendingFollow = null; });
}

/** Follows again the presenter I stopped following. */
export function followOffer(): void {
  if (!collab.offer) return;
  declined.delete(collab.offer.conn);
  follow(collab.offer);
}

/** Stops following (until I choose to follow again). */
export function stopFollowing(): void {
  const f = collab.following;
  if (!f) return;
  declined.add(f.conn);
  if (conn) post('/api/live/follow', { conn, presenter: null });
  setCollab({ following: null, offer: f });
}

/** Who is on my screen changed: follow whoever starts presenting there (unless I said no). */
function onRoomPeople(people: LivePerson[]): void {
  if (collab.presenting || collab.following || pendingFollow) return;
  const presenter = people.find(p => p.presentingConn && p.userId !== myUserId);
  const target = presenter ? { conn: presenter.presentingConn!, userId: presenter.userId, name: presenter.name } : null;
  if (!target || refused.has(target.conn)) { if (collab.offer) setCollab({ offer: null }); return; }
  if (declined.has(target.conn)) { if (collab.offer?.conn !== target.conn) setCollab({ offer: target }); return; }
  follow(target);
}

function listenCollab(es: EventSource): void {
  es.addEventListener('hello', (e) => {
    myUserId = JSON.parse((e as MessageEvent).data).userId ?? null;
    // A new stream: the server forgot this tab's presenting/following.
    const wasPresenting = collab.presenting;
    pendingFollow = null;
    presenterView.clear();
    setCollab({ following: null, followers: [], offer: null });
    if (wasPresenting) setTimeout(() => startPresenting(), 0);
    viewListeners.forEach(fn => fn(null));
  });
  es.addEventListener('view', (e) => {
    const { conn: from, patch, full } = JSON.parse((e as MessageEvent).data) as { conn: string; patch: Record<string, unknown>; full?: boolean };
    if (collab.following?.conn !== from && pendingFollow !== from) return;
    if (full) presenterView.clear();
    for (const [key, value] of Object.entries(patch)) presenterView.set(key, value);
    if (full) viewListeners.forEach(fn => fn(null));
    else for (const key of Object.keys(patch)) viewListeners.forEach(fn => fn(key));
  });
  es.addEventListener('follow-end', (e) => {
    const { conn: from } = JSON.parse((e as MessageEvent).data) as { conn: string };
    declined.delete(from);
    if (pendingFollow === from) pendingFollow = null;
    if (collab.following?.conn === from || collab.offer?.conn === from) setCollab({ following: null, offer: null });
    presenterView.clear();
  });
  es.addEventListener('followers', (e) => {
    const { people } = JSON.parse((e as MessageEvent).data) as { people: { userId: number; name: string }[] };
    if (collab.presenting) setCollab({ followers: people });
  });
}

/** The followed presenter's value for a key (undefined: none known). */
export function presenterValue(key: string): unknown {
  return collab.following ? presenterView.get(key) : undefined;
}

/** Calls fn(key) when the followed presenter's view changes (key null: all of it). */
export function onPresenterView(fn: (key: string | null) => void): () => void {
  viewListeners.add(fn);
  return () => { viewListeners.delete(fn); };
}

const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b);

/**
 * useState that follows collaborator mode: when I present, its value is sent to
 * the people following me; when I follow someone, it takes their value (for
 * the same key) as it changes. Keys name what the state is about, e.g.
 * "project:12:topic" or "vjt:12:ms:<id>:open". Values must be JSON
 * (no Set: use an array). key null: a plain useState.
 */
export function useFollowedState<T>(key: string | null, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(initial);
  const valueRef = useRef(value);
  valueRef.current = value;

  // Following: take the presenter's value, now and as it changes — and keep it
  // (a page setting its own default after loading, or a click, goes back to the
  // presenter's; "Ne plus suivre" frees the screen).
  useEffect(() => {
    if (!key) return;
    const apply = () => {
      const v = presenterValue(key);
      if (v !== undefined && !same(v, valueRef.current)) setValue(v as T);
    };
    apply();
    return onPresenterView((k) => { if (k === null || k === key) apply(); });
  }, [key]);
  useEffect(() => {
    if (!key) return;
    const v = presenterValue(key);
    if (v !== undefined && !same(v, value)) setValue(v as T);
  }, [key, value]);

  // Presenting: send mine (on mount too, so the followers see this part as I do).
  useEffect(() => {
    if (key) publishView(key, value);
  }, [key, value]);

  // Known while mounted, to send when I start presenting.
  useEffect(() => {
    if (!key) return;
    const get = () => valueRef.current;
    const set = mountedStates.get(key) ?? new Set<() => unknown>();
    set.add(get);
    mountedStates.set(key, set);
    return () => {
      set.delete(get);
      if (set.size === 0) mountedStates.delete(key);
    };
  }, [key]);

  return [value, setValue];
}
